// store/slices/sfx.ts — admin SFX tag stats slice.
//
// One-shot loadable list (all tracked tags, sorted desc by count, capped
// at 200 server-side). Loaded once on first mount of the Sfx page via the
// audio:tag_stats_query RPC. After that, live sfx_tag_emitted events from
// the reducer bridge bump counts in place so the page updates without a
// refetch.
//
// New tags appear by being appended on first emission. The page sorts at
// render time so order stays correct even after live bumps.

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AdminSfxTagStat, SfxCategory } from "../types";

export interface SfxState {
  loaded: boolean;
  loading: boolean;
  error: string | null;
  stats: AdminSfxTagStat[];
}

const initialState: SfxState = {
  loaded: false,
  loading: false,
  error: null,
  stats: [],
};

interface TagEmittedPayload {
  tag: string;
  category: SfxCategory;
  scenarioId: string;
  count: number;
  lastSeenIso: string;
}

const sfx = createSlice({
  name: "sfx",
  initialState,
  reducers: {
    sfxStatsLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },
    sfxStatsError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
    sfxStatsLoaded(state, action: PayloadAction<AdminSfxTagStat[]>) {
      state.stats = action.payload;
      state.loaded = true;
      state.loading = false;
      state.error = null;
    },
    /**
     * Apply a live sfx_tag_emitted event. If the tag already exists, bump
     * its count + per-scenario count + lastSeen meta. Otherwise append a
     * new row with the broadcast's count as the initial value.
     *
     * The `count` field in the broadcast is the server's post-increment
     * total — we trust it verbatim rather than re-incrementing locally,
     * because the order of events vs the initial query result isn't
     * guaranteed and the server's number is authoritative.
     */
    sfxTagEmitted(state, action: PayloadAction<TagEmittedPayload>) {
      const { tag, category, scenarioId, count, lastSeenIso } = action.payload;
      const existing = state.stats.find((s) => s.tag === tag);
      if (existing) {
        // Defensive: only advance the count if the broadcast carries a
        // strictly larger number. Out-of-order events shouldn't roll the
        // counter backwards.
        if (count > existing.count) {
          existing.count = count;
        }
        existing.category = category;
        existing.lastSeenIso = lastSeenIso;
        existing.lastScenarioId = scenarioId;
        existing.perScenarioCounts = {
          ...existing.perScenarioCounts,
          [scenarioId]: (existing.perScenarioCounts[scenarioId] ?? 0) + 1,
        };
      } else {
        state.stats.push({
          tag,
          category,
          count,
          lastSeenIso,
          lastScenarioId: scenarioId,
          perScenarioCounts: { [scenarioId]: count },
        });
      }
    },
    clear() {
      return initialState;
    },
  },
});

export default sfx;
